<script setup lang="ts">
import { EEventActions } from '@/store/eventStore/EventStoreTypes'
import { EStoreModules } from '@/store/storeType'
import {
    EFiltersDevice,
    EFiltersDeviceKeysValues,
    EFiltersDevicesLabels
} from '@/types/enum/FiltersDeviceEnum'
import { EHubProvider, type EventDetailProvider } from '@/types/enum/HubProviderEnum'
import type { AssociatedDeviceModel, DeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import {
    BaseDialog,
    CheckboxField,
    CollapseItem,
    FullScreenLoader,
    NumberInputField,
    PrimaryButton,
    StackLayout,
    TypographyItem
} from '@rfc/rfc-components-library'
import { EDeviceActions } from '@rfc/rfc-components-library/store'
import { isNullOrUndefined } from '@rfc/rfc-components-library/utils'
import { computed, inject, ref } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

type Props = {
    data: EventModel
}
const props = defineProps<Props>()

type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const open = ref(false)
const loading = ref(false)

const { devices } = inject<EventDetailProvider>(EHubProvider.EVENT_DEVICES, {
    devices: ref([])
})
const filtersData = computed<Record<string, number | null>>({
    get: () => ({ ...(props.data?.data as Record<string, number | null>) }),
    set: value => value
})

const handleConfirm = () => {
    loading.value = true
    store
        ?.dispatch(`${EStoreModules.EVENT}/${EEventActions.SET_EVENT_FILTERS}`, {
            eventId: props.data.id,
            data: {
                ...props.data,
                data: { ...(props.data.data ?? {}), ...filtersData.value }
            },
            name: props.data.name
        })
        .then(() => {
            selectedDevices.value?.length
                ? store
                      ?.dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.MASS_UPDATE_DEVICE}`, {
                          deviceIds: selectedDevices.value,
                          config: filtersData.value
                      })
                      .then(() => emit('refresh'))
                : undefined
            open.value = false
        })
        .finally(() => (loading.value = false))
}

const selectedDevices = ref<number[]>([])
const handleDeviceSelect = (device: AssociatedDeviceModel | DeviceModel, checked: boolean) =>
    checked
        ? (selectedDevices.value = [...selectedDevices.value, device.device_id ?? device.id!])
        : (selectedDevices.value = selectedDevices.value.filter(item =>
              device.device_id ? item !== device.device_id : item !== device.id
          ))

const handleAllSelect = (checked: boolean) =>
    (selectedDevices.value = checked
        ? ((devices?.value ?? [])?.map(device => device.device_id ?? device.id!) ?? [])
        : [])

const allDevicesSelected = computed(() =>
    devices?.value?.every(item =>
        selectedDevices.value?.find(device =>
            item.device_id ? device === item.device_id : device === item.id
        )
    )
)
</script>

<template>
    <FullScreenLoader :loading="loading" />
    <PrimaryButton :label="$t('event.actions.affectFiltersTitle')" @click="open = true" />
    <BaseDialog
        :title="$t('event.actions.affectFiltersTitle')"
        :open="open"
        @confirm="handleConfirm()"
        @close="open = false"
        class="event-checkpoint-component modal-selector-half-width">
        <template #content>
            <StackLayout direction="column" :gap="16">
                <CollapseItem :open="true" withIcon class="collapse-item" index="1">
                    <template #title>
                        <TypographyItem :label="$t('event.actions.affectFilters')" />
                    </template>
                    <template #content>
                        <StackLayout direction="column" class="py-2">
                            <StackLayout
                                direction="column"
                                v-for="(filterKey, keyItem) in EFiltersDevice"
                                :key="keyItem"
                                :gap="8">
                                <TypographyItem :label="filterKey.toUpperCase()" />
                                <StackLayout :gap="16">
                                    <NumberInputField
                                        v-for="(item, key) in EFiltersDeviceKeysValues"
                                        :key="key"
                                        :label="EFiltersDevicesLabels(filterKey, item)"
                                        :modelValue="
                                            filtersData[`${filterKey}${item}`] ?? undefined
                                        "
                                        @change="
                                            filtersData[`${filterKey}${item}`] = !isNullOrUndefined(
                                                $event
                                            )
                                                ? $event
                                                : null
                                        "
                                        class="w-full" />
                                </StackLayout>
                            </StackLayout>
                        </StackLayout>
                    </template>
                </CollapseItem>
                <CollapseItem v-if="devices?.length" withIcon class="collapse-item" index="2">
                    <template #title>
                        <TypographyItem :label="$t('event.actions.affectDevices')" />
                    </template>
                    <template #content>
                        <StackLayout direction="column" :gap="8">
                            <CheckboxField
                                :modelValue="allDevicesSelected"
                                :indeterminate="!!selectedDevices.length && !allDevicesSelected"
                                label="All"
                                @change="handleAllSelect($event ?? false)" />

                            <StackLayout :gap="4" class="device-checkbox-grid">
                                <CheckboxField
                                    v-for="(device, index) in devices"
                                    :key="index"
                                    :label="device?.name"
                                    :modelValue="
                                        selectedDevices.includes(device.device_id ?? device.id!)
                                    "
                                    @change="handleDeviceSelect(device, $event ?? false)"
                                    class="device-checkbox" />
                            </StackLayout>
                        </StackLayout>
                    </template>
                </CollapseItem>
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.collapse-item {
    & :deep(.collapse-title) {
        width: 100%;
        place-content: space-between;
        box-shadow: 0px 0px 3px 1px $primary;
        padding: 4px;
    }
    & :deep(.collapse-content) {
        padding: 8px;
    }
}

.device-checkbox-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}
.device-checkbox {
    white-space: nowrap;
}
</style>
